import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, Heart, Check, X, Zap, Award, TrendingUp, ArrowRight } from 'lucide-react';
import { PRICING_PLANS } from '../config/pricing';
import { redirectToCheckout } from '../config/stripe';
import { useAuth } from '../contexts/AuthContext';
import SignupModal from '../components/pricing/SignupModal';
import toast from 'react-hot-toast';

export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handlePlanSelect = (planId: string) => {
    const plan = PRICING_PLANS.find(p => p.id === planId);
    if (!plan) return;

    if (planId === 'free') {
      if (!user) {
        const searchParams = new URLSearchParams({ plan: 'free' });
        navigate(`/cadastro?${searchParams.toString()}`);
      } else {
        navigate('/dashboard');
      }
      return;
    }

    // For paid plans
    if (!user) {
      setSelectedPlan(planId);
      setShowSignupModal(true);
      return;
    }

    // If user is logged in, redirect to payment link
    try {
      redirectToCheckout(planId, isYearly);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erro ao iniciar o checkout. Por favor, tente novamente.');
    }
  };

  // Function to get the appropriate tag component based on plan
  const getPlanTag = (plan: typeof PRICING_PLANS[0]) => {
    if (!plan.tag) return null;
    
    if (plan.id === 'premium') {
      return (
        <div className="absolute -top-5 inset-x-0 flex justify-center">
          <span className="px-4 py-1 bg-indigo-100 text-indigo-600 text-sm font-medium rounded-full flex items-center shadow-md">
            <Zap className="h-3.5 w-3.5 mr-1 animate-pulse" />
            {plan.tag}
            <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      );
    }
    
    if (plan.id === 'pro') {
      return (
        <div className="absolute -top-5 inset-x-0 flex justify-center">
          <span className="px-4 py-1 bg-green-100 text-green-600 text-sm font-medium rounded-full flex items-center shadow-md">
            <Award className="h-3.5 w-3.5 mr-1" />
            {plan.tag}
            <TrendingUp className="h-3.5 w-3.5 ml-1" />
          </span>
        </div>
      );
    }
    
    return (
      <div className="absolute -top-5 inset-x-0 flex justify-center">
        <span className="px-4 py-1 bg-indigo-100 text-indigo-600 text-sm font-medium rounded-full shadow-md">
          {plan.tag}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-bold text-white mb-6">
            Escolha o Plano Ideal para Você
          </h1>
          <p className="text-xl text-white/90 mb-8">
            Comece grátis e atualize conforme seu crescimento
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4">
            <span className={`text-sm ${!isYearly ? 'text-white' : 'text-white/70'}`}>
              Mensal
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="relative w-14 h-7 bg-indigo-600 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <div
                className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${
                  isYearly ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm ${isYearly ? 'text-white' : 'text-white/70'}`}>
              Anual
            </span>
            <span className="text-sm text-emerald-400 font-medium">
              Economize até 20%
            </span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto relative">
          {/* Premium Plan Indicator */}
          <div className="hidden md:block absolute left-1/2 top-1/3 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              {/* SVG Arrow pointing to Premium plan */}
              <svg width="120" height="60" viewBox="0 0 120 60" className="absolute -left-16 -top-20">
                <path 
                  d="M10,30 Q60,0 110,30" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                  className="animate-pulse"
                />
                <polygon 
                  points="110,30 100,20 100,40" 
                  fill="white" 
                  className="animate-pulse"
                />
                <text x="40" y="15" fill="white" fontSize="12" fontWeight="bold">Mais Popular</text>
              </svg>
            </div>
          </div>
          
          {/* Pro Plan Indicator */}
          <div className="hidden md:block absolute right-1/4 top-1/3 transform translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              {/* SVG Arrow pointing to Pro plan */}
              <svg width="120" height="60" viewBox="0 0 120 60" className="absolute -right-16 -top-20">
                <path 
                  d="M10,30 Q60,0 110,30" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="3" 
                  strokeLinecap="round"
                  className="animate-pulse"
                />
                <polygon 
                  points="110,30 100,20 100,40" 
                  fill="white" 
                  className="animate-pulse"
                />
                <text x="20" y="15" fill="white" fontSize="12" fontWeight="bold">Melhor Custo-Benefício</text>
              </svg>
            </div>
          </div>
          
          {PRICING_PLANS.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-xl transform transition-all duration-300 group hover:scale-[1.03] hover:-translate-y-1 hover:shadow-2xl ${
                plan.popular ? 'z-10' : ''
              }`}
            >
              {/* Custom tag based on plan type */}
              {getPlanTag(plan)}

              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{plan.name}</h3>
                <p className="mt-2 text-gray-500">{plan.description}</p>

                <div className="mt-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      R${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className="ml-2 text-gray-500">
                      /{isYearly ? 'ano' : 'mês'}
                    </span>
                  </div>
                  {isYearly && plan.yearlyDiscount > 0 && (
                    <p className="mt-1 text-sm text-emerald-600">
                      Economia de {plan.yearlyDiscount}% no plano anual
                    </p>
                  )}
                </div>

                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                      ) : (
                        <X className="h-5 w-5 text-gray-300 mt-0.5 mr-3 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-gray-900' : 'text-gray-500'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlanSelect(plan.id)}
                  className={`mt-8 w-full py-3 px-4 rounded-lg font-medium transition-all ${
                    plan.popular
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 transform hover:scale-105'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  {plan.ctaText}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-4xl mx-auto">
          {[
            {
              icon: Shield,
              title: 'Pagamento Seguro',
              description: 'Processado com segurança via Stripe'
            },
            {
              icon: Heart,
              title: 'Suporte Dedicado',
              description: 'Nossa equipe está aqui para garantir sua melhor experiência'
            },
            {
              icon: Clock,
              title: 'Cancele a Qualquer Momento',
              description: 'Sem contratos longos ou taxas ocultas'
            }
          ].map((indicator, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/15 transition-all text-center transform hover:scale-105"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-4">
                <indicator.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {indicator.title}
              </h3>
              <p className="text-white/80">
                {indicator.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Signup Modal */}
      {showSignupModal && selectedPlan && (
        <SignupModal
          onClose={() => setShowSignupModal(false)}
          planName={PRICING_PLANS.find(p => p.id === selectedPlan)?.name || ''}
          planId={selectedPlan}
          isYearly={isYearly}
        />
      )}
    </div>
  );
}